// src/dataList.tsx
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import './dataList.scss';
import { RootState } from '../../store/store';
import { dataService } from '../../services/apiService';
import { Position as DataApi } from '../../store/interfaces';

const DataPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const [dataItems, setDataItems] = React.useState<DataApi[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  const positionTypes = [
    { value: 'SUPERVISOR', label: 'Supervisor' },
    { value: 'FOREMAN', label: 'Foreman' },
    { value: 'WORKER', label: 'Worker' },
    { value: 'OTHER', label: 'Other' }
  ];

  useEffect(() => {
    async function fetchData() {
      if (!user) {
        return;
      }

      setLoading(true);
      setError(null);
      try {
        let data;
        if (user.role === 'SUPERADMIN') {
          // For superadmin, get all data
          data = await dataService.getData(user);
        } else {
          // For regular users, get data by company
          data = await dataService.getDataByCompany(user, user.companyId!);
        }
        setDataItems(data);
      } catch (error) {
        console.error('Get Positions error:', error);
        setError('Failed to load positions');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [user]);

  const handleTypeSelect = async (type: string) => {
    if (!user) {
      setError('No user logged in');
      return;
    }

    // Check if user has a valid token
    if (!user.token) {
      setError('No authentication token found');
      return;
    }

    setError(null);
    setSuccessMessage(null);
    try {
      // Update the first position found to the selected type
      if (dataItems.length > 0) {
        const firstPosition = dataItems[0];
        const updateData = { type: type as 'SUPERVISOR' | 'FOREMAN' | 'WORKER' | 'OTHER' };
        
        const response = await dataService.updateData(user, firstPosition.id!, updateData);
        
        // Update the data in the list
        setDataItems(dataItems.map(item => 
          item.id === firstPosition.id ? response : item
        ));
        
        // Show success message
        const selectedLabel = positionTypes.find(t => t.value === type)?.label;
        setSuccessMessage(`Position updated to ${selectedLabel}!`);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        setError('No positions found to update');
      }
    } catch (error) {
      console.error('Update Position Failed:', error);
      setError('Failed to update position type');
    }
    
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  if (loading) {
    return <p>Fetching positions...</p>;
  }

  return (
    <div className="data-container">
      <div className="position-selector">
        <h3>Select Your Position Type:</h3>
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}
        {successMessage && (
          <div className="success-message">
            <p>{successMessage}</p>
          </div>
        )}
        <div className="dropdown-container">
          <button 
            className="dropdown-button"
            onClick={toggleDropdown}
          >
            Choose Position Type
            <span className="dropdown-arrow">▼</span>
          </button>
          {isDropdownOpen && (
            <div className="dropdown-menu">
              {positionTypes.map((type, index) => (
                <button
                  key={index}
                  className="dropdown-item"
                  onClick={() => handleTypeSelect(type.value)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataPage; 